/*******************************************************************************
 * Copyright (c) 2024 Passport Fintech Inc. All Rights Reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 ******************************************************************************/

import {
  PaginationInfo,
  PaginationInfoApiResponse,
  paginationInfoFromApiResponse,
} from '@shared/models/pagination-info';

export interface Role {
  roleId: string;
  accountId: string;
  resourceName: string;
  name: string;
  description: string;
  maxSessionDuration: number;
  canAssume: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * This interface describes how the RoleApiResponse is returned by the API,
 * so we can leverage the strict typing capabilities and not rely on "any"
 */
export interface RoleApiResponse {
  role_id: string;
  account_id: string;
  resource_name: string;
  name: string;
  description: string;
  max_session_duration: number;
  can_assume: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * This interface describes how the UserRoleDetailsApiResponse is returned by the API,
 */
export interface UserRoleDetailsApiResponse {
  roles: RoleApiResponse[];
}

/**
 * This method transforms the RoleApiResponse into our own
 * Role object which our application understands
 * @param response the pagination API response
 * @returns the deserialized Role object
 */
export const roleFromApiResponse = (response: RoleApiResponse): Role => {
  return {
    roleId: response.role_id,
    accountId: response.account_id,
    resourceName: response.resource_name,
    name: response.name,
    description: response.description,
    maxSessionDuration: response.max_session_duration,
    canAssume: response.can_assume,
    createdAt: response.created_at,
    updatedAt: response.updated_at,
  };
};

export interface RolesPagination {
  roles: Role[];
  paginationInfo: PaginationInfo;
}

/**
 * This interface describes how the TeamPaginationApiResponse is returned by the API,
 * so we can leverage the strict typing capabilities and not rely on "any"
 */
export interface RolePaginationApiResponse {
  roles: RoleApiResponse[];
  pagination_info: PaginationInfoApiResponse;
}

/**
 * This method transforms the TeamPaginationApiResponse into our own
 * TeamPagination object which our application understands
 * @param response the pagination API response
 * @returns the deserialized TeamPagination object
 */
export const rolePaginationFromResponse = (
  response: RolePaginationApiResponse
): RolesPagination => {
  return {
    roles: response.roles.map(roleFromApiResponse),
    paginationInfo: paginationInfoFromApiResponse(response.pagination_info),
  };
};
